<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const defaultUnits = [
  { name: 'Gram', symbol: 'g', use: 'Kopi bubuk, tepung, gula, coklat' },
  { name: 'Kilogram', symbol: 'kg', use: 'Bahan yang dibeli per kilo: beras, kentang' },
  { name: 'Mililiter', symbol: 'ml', use: 'Susu, sirup, saus, air' },
  { name: 'Liter', symbol: 'l', use: 'Minyak goreng, sirup kemasan besar' },
  { name: 'Porsi', symbol: 'pcs', use: 'Telur, buah, produk yang dihitung per satuan' },
  { name: 'Sachet', symbol: 'sachet', use: 'Gula sachet, creamer, kopi instan kemasan' },
  { name: 'Botol', symbol: 'botol', use: 'Minuman kemasan, saus botolan' },
  { name: 'Lembar', symbol: 'lembar', use: 'Roti tawar, kertas pembungkus' },
]

const faq = [
  {
    q: 'Apakah satuan yang sudah ada bisa dihapus?',
    a: 'Hanya jika satuan tersebut belum digunakan oleh bahan baku manapun. Jika sudah digunakan, sistem menampilkan error "Satuan masih digunakan". Solusinya: biarkan satuan tersebut tetap ada — menghapus satuan yang masih dipakai bisa merusak data stok.',
  },
  {
    q: 'Apakah simbol satuan harus huruf kecil semua?',
    a: 'Tidak harus, tapi disarankan konsisten. Contoh: "g" lebih umum daripada "G" atau "gr". Simbol yang Anda isi akan muncul di sebelah angka stok, jadi pastikan mudah dibaca.',
  },
  {
    q: 'Apakah satuan "gram" dan "kilogram" berbeda di sistem?',
    a: 'Ya, tetap dua satuan yang berbeda — bahan baku (stok, recipe/BOM) selalu memakai satu "satuan dasar" saja dan itu tidak berubah. Tapi sejak fitur Satuan Turunan ada, Anda bisa menghubungkan Kilogram sebagai turunan dari Gram (faktor 1000), sehingga saat Purchase Order / Receiving / Set Stok Awal, Anda boleh input pembelian dalam Kilogram dan sistem otomatis mengonversinya ke gram di balik layar. Stok dan recipe bahan itu sendiri tetap selalu dalam gram.',
  },
  {
    q: 'Saya punya toko laundry, satuan apa yang cocok?',
    a: 'Untuk laundry, satuan yang umum digunakan: "kg" (kilogram) untuk pakaian, "ml" atau "tutup" untuk deterjen cair, "gr" untuk deterjen bubuk, "lembar" untuk plastik pembungkus. Tambahkan satuan yang sesuai dengan operasional toko Anda.',
  },
  {
    q: 'Bisakah dua bahan baku memakai satuan yang berbeda?',
    a: 'Bisa. Setiap bahan baku memiliki satuannya masing-masing. Contoh: Biji Kopi menggunakan "gram", Susu UHT menggunakan "ml", Gelas Plastik menggunakan "pcs". Tidak ada batasan.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <RouterLink
            to="/userguide/sistem/pengaturan"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            Settings
          </RouterLink>
          <VIcon
            icon="ri-arrow-right-s-line"
            size="16"
            class="text-medium-emphasis"
          />
          <span class="text-body-2">Satuan</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar
            color="warning"
            variant="tonal"
            size="56"
            rounded="lg"
            class="me-3"
          >
            <VIcon
              icon="ri-scales-2-line"
              size="30"
            />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">
              Manajemen Satuan
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelola satuan ukuran untuk bahan baku toko Anda
            </p>
          </div>
        </div>

        <VAlert
          type="info"
          variant="tonal"
          icon="ri-flag-line"
        >
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda membuat dan mengatur satuan ukuran yang akan digunakan
          saat input bahan baku. Satuan harus dibuat sebelum Anda bisa menambah
          bahan baku di Modul 3.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-book-open-line"
              class="me-2"
              color="warning"
            />
            Apa itu Satuan dan Mengapa Penting?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Satuan</strong> adalah cara mengukur jumlah bahan baku.
              Sama seperti di pasar: beras dijual per kilogram, susu per liter,
              telur per butir. Di PintuKasir, setiap bahan baku harus punya satuan
              agar sistem tahu "berapa yang dipakai" saat ada transaksi.
            </p>

            <p class="text-body-2 mb-3">
              Contoh konkret: Anda punya bahan baku "Kopi Arabika" dengan satuan <strong>gram</strong>.
              Di recipe produk "Americano", Anda bisa tulis "pakai 20 gram kopi per porsi".
              Setiap kali kasir jual 1 Americano, stok kopi otomatis berkurang 20 gram.
            </p>

            <VAlert
              type="success"
              variant="tonal"
              class="mb-0"
            >
              <strong>Kabar baik:</strong> PintuKasir sudah menyiapkan 8 satuan umum
              secara otomatis saat Anda pertama kali setup toko.
              Dalam banyak kasus Anda tidak perlu tambah satuan baru sama sekali.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Satuan Bawaan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-gift-line"
              class="me-2"
              color="success"
            />
            Satuan yang Sudah Tersedia (Bawaan Sistem)
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Saat toko Anda pertama kali disetup, sistem otomatis membuat 8 satuan ini.
              Anda bisa langsung pakai tanpa perlu tambah manual.
            </p>

            <VTable>
              <thead>
                <tr>
                  <th>Nama Satuan</th>
                  <th style="min-width: 80px">
                    Simbol
                  </th>
                  <th>Cocok Digunakan Untuk</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="unit in defaultUnits"
                  :key="unit.symbol"
                >
                  <td>
                    <strong>{{ unit.name }}</strong>
                  </td>
                  <td>
                    <VChip
                      color="primary"
                      variant="tonal"
                      size="small"
                    >
                      {{ unit.symbol }}
                    </VChip>
                  </td>
                  <td class="text-body-2 text-medium-emphasis">
                    {{ unit.use }}
                  </td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Satuan Turunan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-git-merge-line"
              class="me-2"
              color="primary"
            />
            ✨ Satuan Turunan — Beli dalam Kilogram, Stok Tetap dalam Gram
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Setiap bahan baku punya <strong>satu satuan dasar</strong> yang tidak berubah
              (dipakai untuk stok dan recipe/BOM — lihat panduan
              <RouterLink to="/userguide/produk-inventory/bahan-baku">Bahan Baku</RouterLink>).
              Tapi Anda tidak selalu belanja dalam satuan sekecil itu — kopi biasanya
              dibeli per Kilogram, minyak per Liter, walaupun stoknya dicatat per gram/mililiter.
            </p>
            <p class="text-body-2 mb-3">
              <strong>Satuan Turunan</strong> menjembatani ini: Anda hubungkan satuan besar
              (mis. Kilogram) ke satuan dasar (mis. Gram) beserta <strong>faktor konversinya</strong>
              (1 Kilogram = 1.000 Gram). Setelah dihubungkan, satuan besar itu jadi bisa dipilih
              sebagai "Satuan Pembelian" saat <strong>Set Stok Awal</strong>,
              <RouterLink to="/userguide/pembelian/purchase-order">Purchase Order</RouterLink>,
              maupun <RouterLink to="/userguide/pembelian/receiving">Receiving</RouterLink> —
              sistem otomatis mengonversi ke satuan dasar dan menghitung HPP-nya, tanpa Anda
              perlu hitung manual.
            </p>
            <VAlert type="success" variant="tonal" class="mb-0">
              <strong>Cara tercepat:</strong> gunakan chip <strong>"Satuan Umum"</strong>
              saat tambah satuan — chip Kilogram dan Liter otomatis terhubung ke Gram dan
              Mililiter (faktor 1000) kalau kedua satuan dasar itu sudah ada di toko Anda.
              Tidak perlu isi faktor konversi manual.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Tambah -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-add-circle-line"
              class="me-2"
              color="success"
            />
            Cara Menambah Satuan Baru
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-4">
              Hanya tambah satuan baru jika Anda butuh satuan yang tidak ada dalam daftar bawaan.
              Contoh: toko barbershop mungkin butuh satuan "botol kecil" atau "ml" khusus.
            </p>

            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Inventory → Satuan</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Klik tombol <strong>"+ Tambah Satuan"</strong> di pojok kanan atas
              </li>
              <li class="mb-3">
                Di bagian atas dialog, ada <strong>chip satuan umum</strong> yang bisa di-klik
                untuk mengisi form otomatis. Klik chip yang sesuai, lalu langsung klik Simpan.
              </li>
              <li class="mb-3">
                Jika satuan yang Anda butuhkan tidak ada di chip, isi manual:
                <VTable class="mt-2 mb-2">
                  <thead>
                    <tr>
                      <th style="min-width: 120px">
                        Field
                      </th>
                      <th style="min-width: 80px">
                        Wajib?
                      </th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Nama Satuan</strong>
                      </td>
                      <td>
                        <VChip
                          color="error"
                          size="x-small"
                          variant="tonal"
                        >
                          ✅ Wajib
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Nama lengkap satuan yang mudah dipahami.
                        <br>
                        Contoh: <strong>"Sendok Makan"</strong>, <strong>"Bungkus"</strong>,
                        <strong>"Ikat"</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Simbol</strong>
                      </td>
                      <td>
                        <VChip
                          color="error"
                          size="x-small"
                          variant="tonal"
                        >
                          ✅ Wajib
                        </VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Singkatan/kode pendek yang muncul di tabel stok dan struk.
                        <br>
                        Contoh: <strong>"sdm"</strong>, <strong>"bks"</strong>,
                        <strong>"ikt"</strong>
                        <br>
                        <span class="text-error text-caption">
                          Simbol harus unik — tidak boleh sama dengan simbol lain di toko Anda.
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-0">
                <p class="mb-2">
                  Kalau satuan baru ini adalah satuan besar dari satuan dasar yang sudah ada
                  (mis. Kilogram dari Gram), aktifkan toggle
                  <strong>"Ini satuan turunan (konversi dari satuan lain)"</strong>, lalu isi:
                </p>
                <VTable class="mt-2 mb-2">
                  <thead>
                    <tr>
                      <th style="min-width: 120px">Field</th>
                      <th>Penjelasan & Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Satuan Dasar</strong></td>
                      <td class="text-body-2 py-2">
                        Satuan dasar yang jadi acuan konversi. Hanya menampilkan satuan yang
                        <em>belum</em> menjadi turunan satuan lain (satu tingkat saja, tidak
                        bisa berantai). Contoh: <strong>"Gram"</strong>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Faktor Konversi</strong></td>
                      <td class="text-body-2 py-2">
                        Berapa satuan dasar yang setara dengan 1 satuan ini.
                        Contoh: Kilogram → isi <strong>"1000"</strong> (artinya 1 Kilogram
                        = 1.000 Gram)
                      </td>
                    </tr>
                  </tbody>
                </VTable>
                <p class="mb-0">
                  Klik <strong>"Simpan"</strong> — satuan langsung tersedia untuk dipilih
                  saat input bahan baku, dan (kalau turunan) sebagai Satuan Pembelian di
                  Stok Awal/Purchase Order/Receiving ✅
                </p>
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Edit & Hapus -->
    <VRow class="mt-4">
      <VCol
        cols="12"
        md="6"
      >
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-edit-line"
              class="me-2"
              color="warning"
            />
            Edit Satuan
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                Klik ikon <strong>pensil (✏️)</strong> di baris satuan yang ingin diubah
              </li>
              <li class="mb-2">
                Ubah nama atau simbol
              </li>
              <li class="mb-2">
                Klik <strong>"Update"</strong>
              </li>
            </ol>
            <VAlert
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-2 text-body-2"
            >
              Mengubah simbol satuan yang sudah dipakai bahan baku akan langsung
              mengubah tampilan di semua tempat yang menampilkan satuan tersebut.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        md="6"
      >
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-delete-bin-line"
              class="me-2"
              color="error"
            />
            Hapus Satuan
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-2">
                Klik ikon <strong>tempat sampah (🗑️)</strong>
              </li>
              <li class="mb-0">
                Konfirmasi hapus
              </li>
            </ol>
            <VAlert
              type="error"
              variant="tonal"
              density="compact"
              class="mt-3 text-body-2"
            >
              <strong>Tidak bisa dihapus</strong> jika satuan sudah digunakan
              oleh bahan baku. Ini untuk menjaga integritas data stok Anda.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard
          color="warning"
          variant="tonal"
        >
          <VCardTitle class="pa-4 pb-2">
            <VIcon
              icon="ri-lightbulb-line"
              class="me-2"
            />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Satuan dasar bahan baku tidak boleh diganti setelah dipakai.</strong>
                  Jika Anda mulai pakai "gram" untuk kopi, satuan dasar bahan itu tetap gram
                  selamanya (stok, recipe/BOM semua dalam gram) — ini beda dari Satuan
                  Pembelian saat belanja, yang boleh Kilogram kalau sudah dihubungkan
                  sebagai Satuan Turunan (lihat kartu di atas).
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Satuan dasar terkecil lebih baik untuk akurasi.</strong>
                  Pilih "gram" daripada "kilogram" sebagai satuan dasar bahan agar
                  pengurangan stok per transaksi lebih presisi (20 gram, bukan 0.02
                  kilogram) — Anda tetap bisa beli dalam Kilogram lewat Satuan Turunan.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Hubungkan satuan belanja sehari-hari sebagai Satuan Turunan.</strong>
                  Jika supplier selalu jual per Kilogram atau Liter, hubungkan satuan itu
                  ke satuan dasar bahan Anda sekali saja. Setelahnya, staf pembelian cukup
                  input sesuai nota — tidak perlu hitung konversi ke gram/mililiter manual.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          ❓ Pertanyaan yang Sering Ditanyakan
        </h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="(item, i) in faq"
            :key="i"
          >
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">
                {{ item.a }}
              </p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">
          🔗 Langkah Selanjutnya
        </h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Kategori', sub: 'Buat kategori produk jika belum', icon: 'ri-price-tag-3-line', color: 'primary', to: '/userguide/produk-inventory/kategori' },
              { title: 'Bahan Baku', sub: 'Setelah satuan siap, input bahan baku', icon: 'ri-archive-line', color: 'info', to: '/userguide/produk-inventory/bahan-baku' },
              { title: 'Pengaturan Umum', sub: 'Konfigurasi pajak & struk', icon: 'ri-equalizer-line', color: 'success', to: '/userguide/sistem/pengaturan' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink
              :to="link.to"
              class="text-decoration-none"
            >
              <VCard
                variant="outlined"
                hover
              >
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar
                    :color="link.color"
                    variant="tonal"
                    size="40"
                    rounded
                  >
                    <VIcon
                      :icon="link.icon"
                      size="22"
                    />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">
                      {{ link.title }}
                    </p>
                    <p class="text-caption text-medium-emphasis mb-0">
                      {{ link.sub }}
                    </p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
